import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './AdminMedia.css';

const API_URL = process.env.REACT_APP_API_URL || '';
const IMAGE_EXTENSIONS = new Set(['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg']);

function getAlt(imgPath) {
    if (typeof imgPath !== 'string') return 'image';
    return imgPath.split('/').pop().replace(/\.[^.]+$/, '') || 'image';
}

function AdminMedia() {
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadedUrl, setUploadedUrl] = useState('');
    const [error, setError] = useState('');
    const [previewUrl, setPreviewUrl] = useState('');
    const [images, setImages] = useState([]);
    const [loadingImages, setLoadingImages] = useState(true);
    const [deletingImage, setDeletingImage] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) navigate('/admin/login');
    }, [navigate]);

    // Fetch all images from the server
    const fetchImages = useCallback(async () => {
        setLoadingImages(true);
        try {
            const res = await fetch(`${API_URL}/api/images`);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            if (!Array.isArray(data)) throw new Error('Invalid response');
            const valid = data.filter(
                item => typeof item === 'string' &&
                    IMAGE_EXTENSIONS.has(item.substring(item.lastIndexOf('.')).toLowerCase())
            );
            setImages(valid);
        } catch (err) {
            console.error('Failed to fetch images:', err);
        } finally {
            setLoadingImages(false);
        }
    }, []);

    useEffect(() => {
        fetchImages();
    }, [fetchImages]);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            if (previewUrl) URL.revokeObjectURL(previewUrl);
            setFile(selectedFile);
            setError('');
            setUploadedUrl('');
            setPreviewUrl(URL.createObjectURL(selectedFile));
        }
    };

    const handleUpload = async () => {
        if (!file) { setError('Please select a file first'); return; }

        setUploading(true);
        setError('');
        const formData = new FormData();
        formData.append('image', file);
        const token = localStorage.getItem('token');

        try {
            const response = await fetch(`${API_URL}/api/upload`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });
            const data = await response.json();

            if (response.status === 401) {
                localStorage.removeItem('token');
                navigate('/admin/login');
                return;
            }
            if (response.ok) {
                setUploadedUrl(data.path);
                setError('');
                fetchImages(); // Refresh gallery
            } else {
                setError(data.error || 'Upload failed');
                setFile(null);
                if (previewUrl) URL.revokeObjectURL(previewUrl);
                setPreviewUrl('');
            }
        } catch (err) {
            setError('Network error. Please try again.');
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteClick = (imgPath) => {
        setDeleteConfirm(imgPath); // Show confirm for this image
    };

    const handleDeleteConfirm = async (imgPath) => {
        const filename = imgPath.split('/').pop();
        setDeletingImage(imgPath);
        setDeleteConfirm(null);
        const token = localStorage.getItem('token');

        try {
            const response = await fetch(`${API_URL}/api/images/${encodeURIComponent(filename)}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.status === 401) {
                localStorage.removeItem('token');
                navigate('/admin/login');
                return;
            }
            if (response.ok) {
                setImages(prev => prev.filter(img => img !== imgPath));
                // Clear uploaded URL if it was this image
                if (uploadedUrl === imgPath) setUploadedUrl('');
            } else {
                const data = await response.json();
                setError(data.error || 'Failed to delete image');
            }
        } catch (err) {
            setError('Network error. Please try again.');
        } finally {
            setDeletingImage(null);
        }
    };

    const handleReset = () => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setFile(null);
        setUploadedUrl('');
        setError('');
        setPreviewUrl('');
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        navigate('/admin/login');
    };

    return (
        <div className="admin-media">
            <div className="media-container">

                {/* Header */}
                <div className="media-header">
                    <div>
                        <h1>📁 Media Manager</h1>
                        <p className="media-subtitle">Upload and manage images</p>
                    </div>
                    <div className="header-actions">
                        <Link to="/admin" className="nav-btn nav-btn-admin">
                            ← Admin Panel
                        </Link>
                        <Link to="/" className="nav-btn nav-btn-website">
                            🌐 View Website
                        </Link>
                        <button onClick={handleLogout} className="logout-button">
                            Logout
                        </button>
                    </div>
                </div>

                {/* Error banner */}
                {error && (
                    <div className="error-message">
                        {error}
                        <button onClick={() => setError('')} className="error-close">✕</button>
                    </div>
                )}

                {/* Upload Section */}
                <div className="upload-section">
                    <h2>Upload New Image</h2>

                    <div className="file-input-wrapper">
                        <input
                            type="file"
                            id="fileInput"
                            accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                            onChange={handleFileChange}
                            disabled={uploading}
                        />
                        <label htmlFor="fileInput" className="file-label">
                            {file ? `📎 ${file.name}` : '+ Choose Image'}
                        </label>
                    </div>

                    <p className="file-info">JPEG, PNG, GIF, WebP, SVG — Max 10MB</p>

                    {previewUrl && !uploadedUrl && (
                        <div className="preview-section">
                            <h3>Preview</h3>
                            <img src={previewUrl} alt="Preview" className="preview-image" />
                        </div>
                    )}

                    <button
                        onClick={handleUpload}
                        className="upload-button"
                        disabled={!file || uploading}
                    >
                        {uploading ? 'Uploading...' : '⬆ Upload Image'}
                    </button>
                </div>

                {/* Upload Success */}
                {uploadedUrl && (
                    <div className="success-section">
                        <h2>✓ Upload Successful</h2>
                        <div className="uploaded-preview">
                            <img src={`${API_URL}${uploadedUrl}`} alt="Uploaded" />
                        </div>
                        <p className="success-hint">
                            Go to the{' '}
                            <Link to="/admin" className="success-link">Content Editor</Link>
                            , open a field and pick this image from the gallery.
                        </p>
                        <button onClick={handleReset} className="reset-button">
                            Upload Another Image
                        </button>
                    </div>
                )}

                {/* Image Gallery */}
                <div className="gallery-section">
                    <div className="gallery-header">
                        <h2>All Images ({images.length})</h2>
                        <button onClick={fetchImages} className="refresh-btn" disabled={loadingImages}>
                            {loadingImages ? 'Loading...' : '↻ Refresh'}
                        </button>
                    </div>

                    {loadingImages ? (
                        <div className="gallery-loading">Loading images...</div>
                    ) : images.length === 0 ? (
                        <div className="gallery-empty">No images uploaded yet.</div>
                    ) : (
                        <div className="gallery-grid">
                            {images.map((imgPath) => {
                                const filename = imgPath.split('/').pop();
                                const isDeleting = deletingImage === imgPath;
                                const isConfirming = deleteConfirm === imgPath;

                                return (
                                    <div key={imgPath} className={`gallery-item ${isDeleting ? 'deleting' : ''}`}>
                                        <img
                                            src={`${API_URL}${imgPath}`}
                                            alt={getAlt(imgPath)}
                                            className="gallery-thumb"
                                        />
                                        <div className="gallery-item-info">
                                            <span className="gallery-filename" title={filename}>
                                                {filename}
                                            </span>
                                        </div>

                                        {/* Delete confirmation */}
                                        {isConfirming ? (
                                            <div className="delete-confirm">
                                                <p>Delete this image?</p>
                                                <div className="delete-confirm-btns">
                                                    <button
                                                        onClick={() => handleDeleteConfirm(imgPath)}
                                                        className="confirm-yes"
                                                    >
                                                        Yes, Delete
                                                    </button>
                                                    <button
                                                        onClick={() => setDeleteConfirm(null)}
                                                        className="confirm-no"
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <button
                                                onClick={() => handleDeleteClick(imgPath)}
                                                className="delete-btn"
                                                disabled={isDeleting}
                                                title="Delete image"
                                            >
                                                {isDeleting ? '...' : '🗑 Delete'}
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Instructions */}
                <div className="instructions-section">
                    <h3>How to use</h3>
                    <ol>
                        <li>Select an image and click <strong>Upload Image</strong></li>
                        <li>Go to <strong>Admin Panel</strong> → open a content field</li>
                        <li>Pick the image from the gallery</li>
                        <li>Click <strong>Save Changes</strong></li>
                        <li>To delete an image, click <strong>🗑 Delete</strong> and confirm</li>
                    </ol>
                </div>

            </div>
        </div>
    );
}

export default AdminMedia;
